import React, { useState, useEffect, useRef } from 'react';
import { X, Code, Eye, EyeOff, AlertCircle, Check, Monitor, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import HtmlCodeArea from './HtmlCodeArea';

interface FullHtmlModalProps {
  isOpen: boolean;
  onClose: () => void;
  html: string;
  onSave: (html: string) => void;
}

// Editor de HTML COMPLETO para páginas importadas, em TELA CHEIA com duas
// colunas: código à esquerda e preview ao vivo à direita (pedido do Turista FC,
// no padrão RD: editar e ver o resultado sem trocar de aba nem rolar a tela).
const FullHtmlModal: React.FC<FullHtmlModalProps> = ({ isOpen, onClose, html, onSave }) => {
  const { t } = useTranslation();
  const [code, setCode] = useState(html);
  const [saved, setSaved] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [mobilePreview, setMobilePreview] = useState(false);
  // Preview com debounce: srcDoc a cada tecla recarregaria o iframe sem parar.
  // previewVersion vira a key do iframe: cada versão monta um iframe NOVO com uma
  // única atribuição de srcdoc — trocar o srcdoc do mesmo iframe duas vezes em
  // sequência faz o Chrome abortar a navegação e o preview ficava em branco.
  const [previewCode, setPreviewCode] = useState(html);
  const [previewVersion, setPreviewVersion] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isOpen) {
      setCode(html);
      setPreviewCode(html);
      setPreviewVersion((v) => v + 1);
      setSaved(false);
    }
  }, [isOpen, html]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      // Bump SEMPRE que um preview é commitado (fora do updater: efeito colateral
      // dentro de setState quebra em cenários de re-execução do updater).
      setPreviewCode(code);
      setPreviewVersion((v) => v + 1);
    }, 450);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [code]);

  // Trava o scroll do body enquanto o editor de tela cheia está aberto
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(code);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  };

  return (
    <div className="fixed inset-0 z-50 bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 bg-gray-900">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <Code className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-white truncate">
              {t('pageBuilder.fullHtml.title', 'Editar HTML completo')}
            </h2>
            <p className="text-gray-500 text-xs truncate">
              {t('pageBuilder.fullHtml.subtitleSplit', 'Código à esquerda, resultado ao vivo à direita. Formulários, rastreamento e pixels são injetados na publicação.')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setMobilePreview((v) => !v)}
            title={mobilePreview ? t('pageBuilder.fullHtml.desktopView', 'Preview desktop') : t('pageBuilder.fullHtml.mobileView', 'Preview celular')}
            className={`hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              mobilePreview ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/40' : 'text-gray-400 border border-gray-700 hover:text-white hover:bg-gray-800'
            }`}
          >
            {mobilePreview ? <Smartphone className="w-3.5 h-3.5" /> : <Monitor className="w-3.5 h-3.5" />}
            {mobilePreview ? t('pageBuilder.fullHtml.mobile', 'Celular') : 'Desktop'}
          </button>
          <button
            onClick={() => setShowPreview((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-400 border border-gray-700 hover:text-white hover:bg-gray-800 transition-colors"
          >
            {showPreview ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showPreview ? t('pageBuilder.fullHtml.hidePreview', 'Ocultar preview') : t('pageBuilder.fullHtml.showPreview', 'Mostrar preview')}
          </button>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Duas colunas: código | preview ao vivo */}
      <div className="flex-1 flex min-h-0">
        <div className={`flex flex-col min-w-0 ${showPreview ? 'w-1/2 border-r border-gray-800' : 'w-full'}`}>
          <div className="px-4 pt-3">
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-blue-300 text-[11px] leading-snug">
                {t('pageBuilder.fullHtml.help', 'Cole ou edite o HTML completo (com <!DOCTYPE html>, <head> e <body>). Ao publicar, a DevSkin injeta a captura de formulários, o rastreamento e os pixels vinculados sem alterar o seu design.')}
              </p>
            </div>
          </div>
          <HtmlCodeArea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            dark
            className="flex-1 m-4 mt-3 bg-gray-950 border border-gray-700 rounded-xl focus-within:border-emerald-500 focus-within:ring-1 focus-within:ring-emerald-500"
            placeholder={'<!DOCTYPE html>\n<html lang="pt-BR">\n  <head>...</head>\n  <body>...</body>\n</html>'}
          />
        </div>

        {showPreview && (
          <div className="w-1/2 flex flex-col min-w-0 bg-gray-900">
            <div className="px-4 py-2 text-[11px] text-gray-500 border-b border-gray-800 flex items-center gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              {t('pageBuilder.fullHtml.livePreview', 'Preview ao vivo: atualiza sozinho enquanto você digita')}
            </div>
            <div className="flex-1 p-4 min-h-0 flex justify-center">
              <iframe
                key={previewVersion}
                title="Full HTML preview"
                className={`h-full border border-gray-700 rounded-xl bg-white ${mobilePreview ? 'w-[390px] max-w-full' : 'w-full'}`}
                srcDoc={previewCode}
              />
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800 bg-gray-900">
        <p className="text-gray-500 text-xs">
          {t('pageBuilder.fullHtml.footer', 'Depois de aplicar, use Salvar/Publicar para atualizar a página.')}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white font-medium transition-colors text-sm"
          >
            {t('common.cancel', 'Cancelar')}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold rounded-xl transition-all flex items-center gap-2 text-sm"
          >
            {saved ? (
              <>
                <Check className="w-4 h-4" />
                {t('pageBuilder.fullHtml.applied', 'Aplicado!')}
              </>
            ) : (
              t('pageBuilder.fullHtml.apply', 'Aplicar')
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FullHtmlModal;
